/**
 * Reporting periods shared by the delivery reports and API traffic pages.
 * A period is "today and the days before it", counted from local midnight,
 * so the figures stay put while the page is open.
 */

export type ReportPeriodDays = 7 | 30 | 90;

export interface ReportPeriodOption {
  days: ReportPeriodDays;
  /** Short label for the period switch. */
  label: string;
}

export const REPORT_PERIODS: readonly ReportPeriodOption[] = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export const DEFAULT_REPORT_PERIOD: ReportPeriodDays = 30;

export function isReportPeriod(value: unknown): value is ReportPeriodDays {
  return value === 7 || value === 30 || value === 90;
}

export interface PeriodRange {
  /** Local midnight at the start of the period. */
  start: Date;
  /** Start of the period of the same length just before this one. */
  previousStart: Date;
}

export function periodRange(days: ReportPeriodDays, now = new Date()): PeriodRange {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  const previousStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() - days);
  return { start, previousStart };
}

/** "the last 30 days" */
export function periodPhrase(days: ReportPeriodDays): string {
  return `the last ${days} days`;
}

/** Local calendar date as YYYY-MM-DD, the format the gateway log expects. */
export function isoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
